import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Reset scroll position on route change.
//
// React Router deliberately preserves scroll across navigations, which is right
// for a back button and wrong for following a link: tapping the Loop In pill
// from half-way down the home page opened /download already scrolled into its
// middle.
//
// It has to go through Lenis. SmoothScroll wraps every page in Lenis, which
// takes scrolling over from the browser, so a bare window.scrollTo is not
// reliably honoured while it is running. Lenis first, native as the fallback
// for the reduced-motion path where Lenis is deliberately not started.
//
// A hash is left alone: those navigations mean "go to this section", and
// forcing the top would defeat them.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;

    const toTop = () => {
      const lenis = (
        window as unknown as {
          __lenis?: { scrollTo: (t: number, o?: object) => void };
        }
      ).__lenis;
      // Both, every time. Lenis owns the scroll position while it is running,
      // but the native call is what catches the reduced-motion path where Lenis
      // is deliberately never started.
      lenis?.scrollTo(0, { immediate: true, force: true });
      window.scrollTo(0, 0);
    };

    // Twice: once now, once after the incoming route has laid out. A single
    // synchronous call landed at scrollY 109 rather than 0, because the new
    // page's own height had not settled when it ran.
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
